import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { apiErr } from "@/lib/api";
import { OctonLogo } from "@/components/ui/OctonLogo";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Search, LifeBuoy, ArrowRight } from "lucide-react";

const ARTICLES = [
  { c: "Getting Started", q: "How do I sign up and sign in?", a: "Tap Get started on the home page, choose Client or Trainer, and register with your email and password, or continue with Google. Returning members tap Sign in. You will land straight in your dashboard with OCTON ready." },
  { c: "Getting Started", q: "How do I book a class?", a: "Open Classes & Scheduling from the menu, pick a session and tap Book class. Tap Add to Google Calendar to drop it into your own calendar on any device." },
  { c: "Live Camera Coaching", q: "How does OCTON watch me train?", a: "Open the Train (Live Cam) tab, choose Gym or Yoga, and tap Start session. OCTON uses your device camera to watch your form and streams live coaching cues, speaking them aloud in her own voice. Nothing is recorded or stored." },
  { c: "Live Camera Coaching", q: "Does it work on my phone?", a: "Yes. OCTON GYM is a progressive web app. Open it in your phone browser and choose Add to Home Screen (iOS Safari) or Install app (Android Chrome) to run it full screen like a native app, with the camera and voice working." },
  { c: "OCTON Brain", q: "What is the HNHB brain?", a: "OCTON runs on a Hebbian Neuro-Bayesian brain. Every session encodes concepts about you and updates Bayesian insights held as probabilities, never certainties. Each night it runs a sleep/replay cycle, compresses what it learned and narrates it back to you." },
  { c: "Nutrition & Diet", q: "How does OCTON build my diet plan?", a: "Go to Nutrition, enter your vitals and goal, and tap Generate diet. OCTON calculates your targets and designs a full day of meals with macros from scratch, tuned to your dietary preference and allergies." },
  { c: "Memberships & Payments", q: "How do I manage my membership?", a: "Open Memberships to pick or upgrade a tier, or Payments to see your billing history. Checkout is secured by Stripe. Use test card 4242 4242 4242 4242 while we are in preview." },
  { c: "Account", q: "How do I reset my password?", a: "On the Sign in screen use the same email you registered with. If you signed up with Google, simply continue with Google. For a password reset, contact your trainer or the OCTON team." },
];

export default function HelpCentre() {
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const filtered = ARTICLES.filter((a) => (a.q + a.a + a.c).toLowerCase().includes(q.toLowerCase()));
  const cats = [...new Set(filtered.map((a) => a.c))];

  return (
    <div className="min-h-screen text-white">
      <header className="glass sticky top-0 z-30 px-6 md:px-12 py-4 flex items-center justify-between">
        <button onClick={() => navigate("/")} className="flex items-center gap-2.5" data-testid="help-logo-home">
          <OctonLogo size={32} /><span className="font-heading font-black tracking-tight text-lg">OCTON GYM</span>
        </button>
        <Button onClick={() => navigate("/login")} className="bg-acid text-black hover:bg-[#AACC00] rounded-full">Sign in</Button>
      </header>

      <section className="px-6 md:px-12 py-14 max-w-4xl mx-auto">
        <div className="flex items-center gap-2 mb-3"><LifeBuoy className="w-5 h-5 text-acid" /><span className="mono-label text-acid">help centre</span></div>
        <h1 className="font-heading font-black text-4xl md:text-5xl tracking-tight">How can we help?</h1>
        <p className="text-[#8A91A6] mt-3">Guides for members, athletes and customers of OCTON GYM.</p>
        <div className="relative mt-6 max-w-lg">
          <Search className="w-4 h-4 text-[#565C6F] absolute left-3 top-1/2 -translate-y-1/2" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search help articles…" className="pl-9 h-12 bg-[#0F1115] border-[#1E222A]" data-testid="help-search" />
        </div>

        <div className="mt-10 space-y-8">
          {cats.map((cat) => (
            <div key={cat}>
              <p className="mono-label text-acid mb-2">{cat}</p>
              <Accordion type="single" collapsible className="surface rounded-lg px-4">
                {filtered.filter((a) => a.c === cat).map((a) => (
                  <AccordionItem key={a.q} value={a.q} className="border-[#1E222A]">
                    <AccordionTrigger className="text-left hover:no-underline">{a.q}</AccordionTrigger>
                    <AccordionContent className="text-[#8A91A6] leading-relaxed">{a.a}</AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>
          ))}
          {filtered.length === 0 && <p className="text-[#565C6F]">No articles match “{q}”.</p>}
        </div>

        <BookDemo />
      </section>
    </div>
  );
}

export function BookDemo() {
  const [form, setForm] = useState({ name: "", email: "", goal: "" });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/api/demo", form);
      setDone(true);
      toast.success("Demo booked! The OCTON team will reach out.");
    } catch (err) { toast.error(apiErr(err)); } finally { setBusy(false); }
  };
  return (
    <div className="tracing-beam rounded-xl mt-12" id="book-demo">
      <div className="tracing-beam-inner rounded-xl" />
      <div className="relative p-8 md:p-10">
        <h2 className="font-heading font-black text-2xl md:text-3xl tracking-tight">Book a demo</h2>
        <p className="text-[#8A91A6] mt-2 mb-6 max-w-lg">See OCTON in action. Try live camera coaching, the HNHB brain and AI diet plans with an OCTON specialist.</p>
        {done ? (
          <p className="text-acid font-medium" data-testid="demo-done">Thanks — we'll be in touch shortly. Meanwhile, tap Sign in to start training now.</p>
        ) : (
          <form onSubmit={submit} className="grid sm:grid-cols-3 gap-3 max-w-2xl">
            <Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Your name" className="bg-[#0F1115] border-[#1E222A] h-11" data-testid="demo-name" />
            <Input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="Email" className="bg-[#0F1115] border-[#1E222A] h-11" data-testid="demo-email" />
            <Input value={form.goal} onChange={(e) => setForm({ ...form, goal: e.target.value })} placeholder="Your goal (optional)" className="bg-[#0F1115] border-[#1E222A] h-11" data-testid="demo-goal" />
            <Button type="submit" disabled={busy} className="bg-acid text-black hover:bg-[#AACC00] h-11 rounded-md sm:col-span-3" data-testid="demo-submit">
              {busy ? "Booking…" : "Book my demo"} <ArrowRight className="w-4 h-4" />
            </Button>
          </form>
        )}
      </div>
    </div>
  );
}
